import { useState, useMemo } from 'react'
import { Link } from 'react-router'
import { Calculator, ArrowRight, Info } from 'lucide-react'

/**
 * Inline price estimator widget for /prix/$slug pages.
 * User drags 2 sliders (surface + quality tier) → live estimate updates.
 * Anchors programmatic price range with interactive UX that outperforms
 * static tables in dwell time + CTA clicks.
 *
 * Math: estimate = baseRate * surface * qualityMult[tier].
 * When priceBand (real leads data) is available, prefer its median as
 * baseRate — that is the proprietary signal competitors can't match.
 *
 * Props:
 *   basePrice — SERVICE_PRICES.min..max fallback
 *   priceBand — optional proprietary { min, median, max } from DB leads
 *   serviceSlug, citySlug — used to deep-link CTA to pre-filled devis form
 */
interface PriceBand {
  min: number
  median: number
  max: number
}

interface PriceEstimatorProps {
  serviceName: string
  artisanName: string
  cityName: string
  serviceSlug: string
  citySlug: string
  unit: string // 'm²' | 'intervention' | 'pièce' etc
  basePrice: { min: number; max: number }
  priceBand?: PriceBand | null
}

const QUALITY_TIERS = [
  { id: 'basic', label: 'Basique', mult: 0.7, hint: 'Entrée de gamme' },
  { id: 'standard', label: 'Standard', mult: 1.0, hint: 'Milieu de gamme' },
  { id: 'premium', label: 'Premium', mult: 1.6, hint: 'Haut de gamme' },
] as const

export function PriceEstimator({
  serviceName,
  artisanName: _artisanName,
  cityName,
  serviceSlug,
  citySlug,
  unit,
  basePrice,
  priceBand,
}: PriceEstimatorProps) {
  const isPerUnit = unit === 'm²' || unit === 'h'
  const [surface, setSurface] = useState(isPerUnit ? 50 : 1)
  const [tier, setTier] = useState<'basic' | 'standard' | 'premium'>('standard')

  const estimate = useMemo(() => {
    const t = QUALITY_TIERS.find((q) => q.id === tier)!
    // Prefer proprietary priceBand median when available (data moat)
    const medianBase = priceBand?.median ?? (basePrice.min + basePrice.max) / 2
    const minBase = priceBand?.min ?? basePrice.min
    const maxBase = priceBand?.max ?? basePrice.max

    return {
      min: Math.round(minBase * surface * t.mult),
      mid: Math.round(medianBase * surface * t.mult),
      max: Math.round(maxBase * surface * t.mult),
    }
  }, [surface, tier, basePrice.min, basePrice.max, priceBand])

  const activeTier = QUALITY_TIERS.find((q) => q.id === tier)!

  return (
    <section className="mt-10 overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-white">
          <Calculator className="h-4 w-4" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900">
            Estimez votre {serviceName.toLowerCase()} à {cityName}
          </h2>
          <p className="text-xs text-slate-500">
            {priceBand ? `Basé sur ${priceBand ? '30j de devis réels' : 'tarifs moyens'} à ${cityName}` : 'Calcul indicatif selon les paramètres'}
          </p>
        </div>
      </div>

      {/* Surface slider */}
      {isPerUnit && (
        <div className="mt-6">
          <div className="flex items-center justify-between text-sm">
            <label htmlFor="surface" className="font-semibold text-slate-700">
              {unit === 'h' ? 'Durée estimée' : 'Surface'}
            </label>
            <span className="rounded-lg bg-white px-3 py-1 font-bold text-primary shadow-sm">
              {surface} {unit}
            </span>
          </div>
          <input
            id="surface"
            type="range"
            min={unit === 'h' ? 1 : 5}
            max={unit === 'h' ? 24 : 300}
            step={unit === 'h' ? 1 : 5}
            value={surface}
            onChange={(e) => setSurface(Number(e.target.value))}
            className="mt-2 w-full accent-primary"
          />
          <div className="mt-1 flex justify-between text-[10px] text-slate-400">
            <span>{unit === 'h' ? '1h' : '5 m²'}</span>
            <span>{unit === 'h' ? '24h' : '300 m²'}</span>
          </div>
        </div>
      )}

      {/* Quality tier selector */}
      <div className="mt-6">
        <div className="text-sm font-semibold text-slate-700">Niveau de finition</div>
        <div className="mt-2 grid grid-cols-3 gap-2">
          {QUALITY_TIERS.map((q) => (
            <button
              key={q.id}
              type="button"
              onClick={() => setTier(q.id)}
              className={`rounded-xl border-2 p-3 text-left transition-colors ${
                tier === q.id
                  ? 'border-primary bg-white shadow-sm'
                  : 'border-slate-200 bg-white/50 hover:border-primary/50'
              }`}
            >
              <div className={`text-sm font-bold ${tier === q.id ? 'text-primary' : 'text-slate-700'}`}>
                {q.label}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500">{q.hint}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Result */}
      <div className="mt-6 rounded-xl bg-white p-5 shadow-sm">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Estimation {activeTier.label.toLowerCase()}
        </div>
        <div className="mt-2 flex items-baseline gap-3">
          <span className="text-3xl font-extrabold text-primary">
            {estimate.mid.toLocaleString('fr-FR')} €
          </span>
          <span className="text-sm text-slate-500">
            (fourchette {estimate.min.toLocaleString('fr-FR')}–{estimate.max.toLocaleString('fr-FR')} €)
          </span>
        </div>
        <p className="mt-2 flex items-start gap-1.5 text-xs text-slate-500">
          <Info className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          Estimation indicative. Le prix réel dépend de l'état du chantier et de l'accessibilité.
        </p>
      </div>

      {/* CTA */}
      <Link
        to={`/demande-devis?service=${serviceSlug}&city=${citySlug}&tier=${tier}&surface=${surface}`}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-bold text-white shadow-sm hover:bg-primary-hover"
      >
        Obtenir un devis précis pour ce projet <ArrowRight className="h-4 w-4" />
      </Link>
    </section>
  )
}
